import React, { useState, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { MindMapNode, ColorPalette } from '../../types';
import { getContrastColor } from '../../utils/colorMap';
import { getFontSize, getPadding } from './utils';

export interface NodeEditorProps {
    node: any;
    freshNodeData: MindMapNode; 
    transform: d3.ZoomTransform;
    palette: ColorPalette;
    isMandalaMode: boolean;
    onSave: (val: string) => void;
    onCancel: () => void;
    nodeScale: number;
}

export const NodeEditor: React.FC<NodeEditorProps> = ({ 
    node, 
    freshNodeData,
    transform, 
    palette, 
    isMandalaMode, 
    onSave, 
    onCancel,
    nodeScale
}) => {
    const [value, setValue] = useState(freshNodeData.name);
    const valueRef = useRef(freshNodeData.name);
    const onSaveRef = useRef(onSave);
    const inputRef = useRef<HTMLInputElement>(null);
    const isSavedOrCancelled = useRef(false);

    useEffect(() => { valueRef.current = value; }, [value]);
    useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
    useEffect(() => { setValue(freshNodeData.name); }, [freshNodeData.name]);

    // VIBE CHECK: Removed the unmount cleanup to prevent React 18 Strict Mode
    // from instantly closing the editor. onBlur handles our saves perfectly.
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, []);

    const scale = transform.k;
    const cx = transform.x + node._cartesian.x * scale;
    const cy = transform.y + node._cartesian.y * scale;
    const fs = getFontSize(node); 
    const p = getPadding(node);
    
    let bg = node._color;
    let isTransparent = false;
    if (isMandalaMode && node.depth === 0 && !node.data.isFloating) {
        bg = "transparent"; 
        isTransparent = true;
    } else if (!isMandalaMode && node.depth === 0 && !node.data.isFloating) {
        bg = palette.nodeBackground;
    }
    
    let effectiveBg = bg;
    if (bg === "transparent") effectiveBg = palette.background;
    let textColor = getContrastColor(effectiveBg);
    if (node.depth === 0 && !node.data.isFloating && !isMandalaMode) {
         textColor = getContrastColor(palette.nodeBackground);
    }

    const triggerSave = () => {
        if (isSavedOrCancelled.current) return;
        isSavedOrCancelled.current = true;
        onSave(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            isSavedOrCancelled.current = true; 
            onCancel();
        }
    };

    // Removed aggressive pointer/mouse stopPropagation to allow 
    // native Surface Pro touch/pen focus to function smoothly.
    return (
       <div className="select-text" 
            style={{
               position: 'absolute',
               left: cx,
               top: cy,
               transform: `translate(-50%, calc(-50% - ${node._dims.descHeight ? node._dims.descHeight/2 : 0}px)) scale(${scale * nodeScale})`,
               zIndex: 100,
               pointerEvents: 'auto',
               userSelect: 'text',
               WebkitUserSelect: 'text' as any
           }}>
           <input
             ref={inputRef}
             value={value}
             onChange={(e) => setValue(e.target.value)}
             style={{
                 fontSize: `${fs}px`,
                 fontWeight: node.depth === 0 ? 'bold' : '500',
                 backgroundColor: isTransparent ? palette.background : bg, 
                 color: textColor,
                 minWidth: node._dims.contentWidth + 'px',
                 width: Math.max(node._dims.contentWidth, value.length * fs * 0.6 + p.x * 2) + 'px',
                 height: (node._dims.height - (node._dims.descHeight || 0)) + 'px', 
                 textAlign: 'center',
                 borderRadius: (node._dims.height / 2) + 'px',
                 padding: `${p.y}px ${p.x}px`,
                 boxSizing: 'border-box',
                 pointerEvents: 'auto',
                 userSelect: 'text',
                 WebkitUserSelect: 'text' as any
             }}
             className="outline-none border-2 border-indigo-500 shadow-xl select-text" 
             onKeyDown={handleKeyDown}
             onBlur={triggerSave}
             autoFocus
           />
       </div>
    );
};
